import { Button } from 'components/Button'
import { ArrowLeft, ArrowRight, Plus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useActiveEditingSectionStore, useEditorFocusStore } from '../../store/conversation-store'
import type { InstagramMediaItem } from '../../types'
import { carouselSlideFocusKey, parseCarouselSlideFocus } from '../../utils/carousel-slide-focus'

interface MediaItemStripProps {
  items: InstagramMediaItem[]
  maxItems: number
  onChange: (items: InstagramMediaItem[]) => void
  // Optional: when omitted, the "+ Add" tile is hidden — used when the caller
  // renders an inline ImagePickerBody right below the strip and the tile would
  // be redundant.
  onAddClick?: () => void
  creativeId?: string
}

export function MediaItemStrip({
  items,
  maxItems,
  onChange,
  onAddClick,
  creativeId,
}: MediaItemStripProps) {
  const focusRequest = useEditorFocusStore(s =>
    creativeId ? s.focusRequests[creativeId] : undefined
  )
  const consumeFocus = useEditorFocusStore(s => s.consumeFocus)
  const setActiveSection = useActiveEditingSectionStore(s => s.setActive)
  const clearActiveSection = useActiveEditingSectionStore(s => s.clearActive)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const [highlightedIdx, setHighlightedIdx] = useState<number | null>(null)
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!creativeId || !focusRequest) return
    const idx = parseCarouselSlideFocus(focusRequest)
    if (idx === null || idx >= items.length) return

    consumeFocus(creativeId)

    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    setHighlightedIdx(idx)
    setActiveSection(creativeId, carouselSlideFocusKey(idx))
    requestAnimationFrame(() => {
      slideRefs.current[idx]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      })
    })
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedIdx(null)
      clearActiveSection(creativeId)
      highlightTimerRef.current = null
    }, 1500)
  }, [focusRequest, creativeId, consumeFocus, setActiveSection, clearActiveSection, items.length])

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    }
  }, [])

  const handleRemove = (index: number) => {
    const next = items.filter((_, i) => i !== index)
    onChange(next)
  }

  const handleMove = (index: number, delta: -1 | 1) => {
    const target = index + delta
    if (target < 0 || target >= items.length) return
    const next = items.slice()
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const canAdd = items.length < maxItems

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, idx) => (
        <div
          key={item.attachmentId || `${item.url}-${idx}`}
          ref={el => {
            slideRefs.current[idx] = el
          }}
          className={`group relative h-28 w-28 overflow-hidden rounded-[10px] border bg-bg-3 transition-all ${
            highlightedIdx === idx ? 'border-acid ring-2 ring-acid/40' : 'border-line'
          }`}
        >
          <img src={item.url} alt={item.altText ?? ''} className="h-full w-full object-cover" />

          <span className="absolute top-1 left-1 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {idx + 1}
          </span>

          <Button
            type="button"
            variant="ghost"
            onClick={() => handleRemove(idx)}
            aria-label={`Remove image ${idx + 1}`}
            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/55 p-0 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/75 focus:opacity-100"
          >
            <X size={12} />
          </Button>

          <div className="absolute inset-x-1 bottom-1 flex justify-between opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleMove(idx, -1)}
              disabled={idx === 0}
              aria-label={`Move image ${idx + 1} left`}
              className="h-6 w-6 rounded-full bg-black/55 p-0 text-white hover:bg-black/75 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ArrowLeft size={12} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleMove(idx, 1)}
              disabled={idx === items.length - 1}
              aria-label={`Move image ${idx + 1} right`}
              className="h-6 w-6 rounded-full bg-black/55 p-0 text-white hover:bg-black/75 disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ArrowRight size={12} />
            </Button>
          </div>
        </div>
      ))}

      {onAddClick && (
        <Button
          type="button"
          variant="t3-tertiary"
          onClick={onAddClick}
          disabled={!canAdd}
          className="h-28 w-28 flex-col rounded-[10px] border-2 border-dashed border-line text-xs text-paper-dim"
        >
          <Plus size={18} />
          {canAdd ? `Add (${items.length}/${maxItems})` : 'Max reached'}
        </Button>
      )}
    </div>
  )
}
